
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Settings, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function MissingCredentialsModal() {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);

    // Check config status globally
    const { data: config, isLoading } = useQuery({
        queryKey: ["config"],
        queryFn: () => api.getConfig(),
        // Don't retry indefinitely if backend is up but complaining
        retry: 1,
        // Re-check periodically or on focus to clear the modal when fixed
        refetchOnWindowFocus: true,
    });

    useEffect(() => {
        if (!isLoading && config) {
            // Check if status is partial (missing secrets)
            const isPartial = (config as any).status === "partial";

            // If missing secrets AND not already on config page, show modal
            if (isPartial && location.pathname !== "/v1/config") {
                setIsOpen(true);
            } else {
                setIsOpen(false);
            }
        }
    }, [config, isLoading, location.pathname]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-card w-full max-w-md rounded-xl shadow-2xl border border-border overflow-hidden animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="bg-amber-500/10 px-6 py-4 border-b border-amber-500/20 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-600 dark:text-amber-500">
                        <AlertTriangle className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">Missing Credentials</h3>
                </div>

                {/* Body */}
                <div className="p-6">
                    <p className="text-muted-foreground leading-relaxed">
                        Essential credentials are missing. Features may not work correctly until you configure them.
                    </p>

                    {/* Missing Secrets List (optional/if available) */}
                    {(config as any)?.required_secrets && (config as any).required_secrets.length > 0 && (
                        <div className="mt-4 p-3 bg-muted/50 rounded-lg border border-border">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Missing Keys:</p>
                            <div className="flex flex-wrap gap-2">
                                {(config as any).required_secrets.map((key: string) => (
                                    <span key={key} className="px-2 py-1 text-xs font-mono bg-background border border-border rounded text-foreground">
                                        {key}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer actions */}
                <div className="bg-muted/30 px-6 py-4 flex flex-col sm:flex-row gap-3 justify-end border-t border-border">
                    {/* Dismiss button? User said "raise a popup... add buttons that take them to config page" */}
                    {/* We'll add a 'Dismiss' for non-blocking requirement, allowing them to browse but maybe features fail */}
                    <button
                        onClick={() => setIsOpen(false)}
                        className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                        Dismiss
                    </button>

                    <Link
                        to="/v1/config"
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-bold rounded-lg shadow-sm hover:bg-primary/90 transition-all hover:shadow-md"
                        onClick={() => setIsOpen(false)}
                    >
                        <Settings className="w-4 h-4" />
                        <span>Configure Secrets</span>
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
